import { useMelwaysLink } from '@seek/melways-react';
import { useTranslations } from '@vocab/react';
import { Box, IconHeart, Text, TextLink, IconArrow } from 'braid-design-system';

import { useSelector } from 'src/store/react';
import {
  selectFeatureFlag,
  selectAuthenticated,
  selectRecentSearches,
} from 'src/store/selectors';

import translations from './.vocab';
import { RecentSearchItem } from './RecentSearches/RecentSearchItem';
import { SavedRecentSearchItem } from './SavedRecentSearchItem';
import useSavedSearchesSearchBar, {
  formatSummary,
} from './SavedSearches/useSavedSearchesSearchBar';
import { MAX_RECENT_SEARCHES } from './constants';

import * as styles from './SavedRecentSearches.css';

export const SavedRecentSearches = () => {
  const { t } = useTranslations(translations);
  const getMelwaysLink = useMelwaysLink();
  const isBehaviouralCuesFiltersEnabled = useSelector(
    selectFeatureFlag('behaviouralCuesFilters'),
  );

  // Recent Searches
  const recentSearches = useSelector(selectRecentSearches) || [];
  const recentSearchesToRender = recentSearches.slice(0, MAX_RECENT_SEARCHES);

  // This is for Saved Searches
  const { displayState, savedSearches, totalSavedSearches } =
    useSavedSearchesSearchBar();
  const savedSearchesToRender = savedSearches || [];

  const authenticated = useSelector(selectAuthenticated);
  const savedSearchesDataIsReady = displayState === 'READY';
  const renderSavedSearches =
    authenticated && totalSavedSearches > 0 && savedSearchesDataIsReady;

  const generateSaveSearchUrl = (
    savedSearch: NonNullable<
      ReturnType<typeof useSavedSearchesSearchBar>['savedSearches']
    >[number],
  ): string =>
    getMelwaysLink({
      path: `/jobs?${savedSearch.query.searchQueryString}&savedsearchid=${savedSearch.id}`,
    });

  const seeAllLinkPath = '/my-activity/saved-searches';
  if (recentSearchesToRender.length <= 0 && !renderSavedSearches) {
    return;
  }

  return (
    <Box
      id="SavedRecentSearches"
      background="surface"
      className={styles.savedRecentPanel}
      boxShadow="large"
      paddingBottom="xsmall"
      paddingTop="small"
      tabIndex={-1}
    >
      {recentSearchesToRender.length > 0 && (
        <Box
          className="recentSearches"
          paddingBottom={renderSavedSearches ? 'xxsmall' : 'none'}
        >
          <Box className={styles.menuItem}>
            <Text weight="strong" size="small" tone="secondary">
              {t('Recent searches')}
            </Text>
          </Box>
          <Box className="searchItemSection">
            {recentSearchesToRender.map((recentSearch, index) => (
              <RecentSearchItem
                recentSearch={recentSearch}
                key={`recentSearch-${index}`}
              />
            ))}
          </Box>
        </Box>
      )}

      {renderSavedSearches ? (
        <Box className="savedSearches">
          <Box className={styles.menuItem}>
            <Text weight="strong" size="small" tone="secondary">
              {t('Saved searches')}
            </Text>
          </Box>
          <Box className="searchItemSection">
            {savedSearchesToRender.map((savedSearch, index) => {
              const saveSearchUrl = generateSaveSearchUrl(savedSearch);
              const displayCountLabel = isBehaviouralCuesFiltersEnabled
                ? savedSearch.newToYouCountLabel
                : savedSearch.countLabel;

              return (
                <SavedRecentSearchItem
                  searchItem={{
                    label: savedSearch.name,
                    count:
                      displayCountLabel !== '0'
                        ? t('{displayCountLabel} new', {
                            displayCountLabel: displayCountLabel || '',
                          })
                        : undefined,
                    summary: formatSummary(savedSearch.query.parameters),
                    href: saveSearchUrl,
                  }}
                  icon={<IconHeart />}
                  key={`savedSearch-${index}`}
                />
              );
            })}
          </Box>
          <Box className={styles.menuItem}>
            <Text size="small">
              <TextLink
                href={seeAllLinkPath}
                icon={<IconArrow direction="right" />}
                iconPosition="trailing"
                weight="weak"
              >
                {t('See all ({count})', {
                  count: totalSavedSearches.toString(),
                })}
              </TextLink>
            </Text>
          </Box>
        </Box>
      ) : null}
    </Box>
  );
};
